import { useRef } from "react"
import { useFrame, useThree } from "@react-three/fiber"
import { damp3 } from "maath/easing"
import * as THREE from "three"

import { usePlayerStore } from "../../stores/usePlayerStore"

const FOLLOW_HEIGHT = 1.0
const FOLLOW_SMOOTH = 0.35

// 2026-05-06 v2 — Esfera de seguridad para la posición de cámara.
// Mantiene a la cámara dentro del radio del Domo (28 m al ras del
// piso, 28.13 m al ápice — datos leídos directo de la GLB v3.3).
// Con el WALK_BOUNDARY_RADIUS subido a 26 m, el avatar puede llegar
// casi pegado a la pared. Para que la cámara orbital no salga del
// recinto en ese borde, hacemos DOS cosas en cada frame:
//   1. Si el avatar está cerca del borde, reducimos
//      OrbitControls.maxDistance dinámicamente para que el orbit
//      nunca proyecte la cámara fuera de la esfera de seguridad.
//   2. Como red final de seguridad, post-update clampeamos la
//      posición al CAMERA_SAFE_RADIUS_XZ.
// Sin (1) el clamp duro de (2) pelea con OrbitControls (jitter); con
// (1) OrbitControls "sabe" cuánto puede alejarse y todo queda fluido.
const CAMERA_SAFE_RADIUS_XZ = 27.5
const CAMERA_SAFE_MAX_Y = 14
const CAMERA_SAFE_MIN_Y = 0.6
// Margen por debajo de CAMERA_SAFE_RADIUS_XZ donde la cámara empieza
// a recibir el clamp dinámico — deja un colchón para que OrbitControls
// pueda alejar la cámara sin saltar bruscamente al límite.
const CAMERA_DYNAMIC_PADDING = 0.5
// Distancia mínima que el orbit puede tomar aunque el avatar esté
// pegadísimo a la pared. Por debajo de esto la cámara entra al cuerpo
// del avatar — peor experiencia que tener la cámara cerca.
const CAMERA_MIN_FALLBACK_DISTANCE = 3

// Sprint 6 — Posición y target de cámara cuando estamos en void.
// El target apunta al orbe (que vive en y=4) para que quede en el
// tercio superior del viewport y el texto narrativo abajo no choque.
const VOID_TARGET = new THREE.Vector3(0, 4, 0)
const VOID_CAMERA_POS = new THREE.Vector3(0, 2.5, 12)
const VOID_SMOOTH = 0.6

// 2026-05-05 v6 — Primera persona.
// FP_HEAD_HEIGHT: altura de los "ojos" del avatar (1.65m matchea
// la altura promedio de Mixamo Cristal). FP_LOOK_DISTANCE: target de
// la cámara a 4m delante del avatar — suficientemente lejos para
// que el orbit no descomponga la rotación del lookAt al pasar por
// el target. FP_SMOOTH: damping un poco más rápido que tercera
// persona para que la cámara siga al cuerpo sin lag fuerte cuando
// gira al caminar.
//
// 2026-05-05 v7 — Reescrito para leer yaw/pitch del store en lugar
// del quaternion del avatar. La fuente de verdad es cameraYaw +
// cameraPitch: el Tripulante los modifica con drag (manejado por
// useFpCameraInput); LocalAvatar los reescribe al ángulo del lookAt
// cuando el cuerpo camina hacia un target. Convención: yaw=0 mira
// al +Z (mismo "norte" que el lookAt natural de Object3D), yaw
// positivo gira CCW visto desde arriba. forward = (sin(yaw)*cos(pitch),
// sin(pitch), cos(yaw)*cos(pitch)) — sí, +Z, no -Z: el bug previo
// era que mirábamos hacia atrás del cuerpo.
const FP_HEAD_HEIGHT = 1.65
const FP_LOOK_DISTANCE = 4
const FP_SMOOTH = 0.18

// Sigue al avatar local moviendo el target de OrbitControls (la cámara orbita ese punto).
// El usuario sigue pudiendo girar/zoom libremente; solo el centro de la órbita acompaña.
//
// Sprint 6 v2 — En phase "void" interceptamos: fijamos target hacia
// el orbe (0,4,0) y arrastramos la cámara a una posición frontal
// estable. El OrbitControls ya está deshabilitado en Canvas durante
// void, así que la cámara queda donde la dejamos. Cuando salimos del
// void (descending+) el rig vuelve a su comportamiento de seguimiento.
//
// 2026-05-05 v6 — Tercera rama: cameraMode === "firstPerson".
// Posicionamos la cámara EN la cabeza del avatar y el target a
// FP_LOOK_DISTANCE metros delante de su mirada. OrbitControls queda
// deshabilitado en Canvas para este modo (la cámara ya no orbita,
// "es" el avatar). El cuerpo del propio avatar se oculta en
// LocalAvatar/Canvas para que no aparezca su nuca en pantalla.
export function CameraRig() {
    const controls = useThree((s) => s.controls) as
        | (THREE.EventDispatcher & {
              target: THREE.Vector3
              update: () => void
              minDistance: number
              maxDistance: number
          })
        | null
    const camera = useThree((s) => s.camera)
    const tempTarget = useRef(new THREE.Vector3())
    const tempPos = useRef(new THREE.Vector3())
    const forward = useRef(new THREE.Vector3())
    // Cacheo del max/min "base" del OrbitControls (los que setea el
    // JSX en Canvas). Sirven como techo del clamp dinámico — nunca
    // dejamos al orbit alejarse más de lo que el JSX permitía.
    const baseMaxRef = useRef<number | null>(null)
    const baseMinRef = useRef<number | null>(null)

    useFrame((_, dt) => {
        if (!controls?.target) return
        const state = usePlayerStore.getState()
        const phase = state.phase
        if (phase === "void") {
            damp3(controls.target, VOID_TARGET, VOID_SMOOTH, dt)
            damp3(camera.position, VOID_CAMERA_POS, VOID_SMOOTH, dt)
            controls.update()
            return
        }
        const pos = state.position
        // Capturar los valores base del JSX la primera vez que el
        // OrbitControls esté disponible. A partir de ahí mutamos
        // controls.maxDistance dinámicamente sin perder el techo.
        if (
            baseMaxRef.current == null &&
            typeof controls.maxDistance === "number"
        ) {
            baseMaxRef.current = controls.maxDistance
        }
        if (
            baseMinRef.current == null &&
            typeof controls.minDistance === "number"
        ) {
            baseMinRef.current = controls.minDistance
        }
        if (state.cameraMode === "firstPerson") {
            // Cabeza del avatar.
            tempPos.current.set(
                pos[0],
                pos[1] + FP_HEAD_HEIGHT,
                pos[2]
            )
            // Forward derivado de yaw/pitch del store (drag del
            // Tripulante o sync desde el lookAt al caminar). Yaw=0
            // mira al +Z; pitch=0 horizontal.
            const yaw = state.cameraYaw
            const pitch = state.cameraPitch
            const cosP = Math.cos(pitch)
            forward.current.set(
                Math.sin(yaw) * cosP,
                Math.sin(pitch),
                Math.cos(yaw) * cosP
            )
            tempTarget.current
                .copy(tempPos.current)
                .add(forward.current.multiplyScalar(FP_LOOK_DISTANCE))
            // 2026-05-06 — Anclamos la altura forzando la posición
            // exacta cada frame, sin damp. Antes usábamos damp3 sobre
            // camera.position + controls.update(): ese update interno
            // de OrbitControls reposicionaba la cámara según su
            // spherical interno (que el drag del pitch desfasaba),
            // así que arrastrar arriba/abajo subía/bajaba la cámara
            // poco a poco. Ahora la posición SIEMPRE es la cabeza y
            // la rotación se aplica con un lookAt directo, sin que
            // OrbitControls intervenga. Mantenemos `controls.target`
            // sincronizado a la cabeza para que cuando el Tripulante
            // vuelva a tercera persona, OrbitControls orbite el
            // mismo punto donde estaba mirando.
            camera.position.copy(tempPos.current)
            camera.lookAt(tempTarget.current)
            controls.target.copy(tempPos.current)
            return
        }
        // Tercera persona — comportamiento original.
        tempTarget.current.set(pos[0], FOLLOW_HEIGHT, pos[2])
        damp3(controls.target, tempTarget.current, FOLLOW_SMOOTH, dt)

        // 2026-05-06 v3 — Clamp dinámico de OrbitControls.maxDistance
        // según la distancia del avatar al origen. Al ras del centro
        // del Domo el orbit puede usar todo su rango (hasta el max
        // del JSX). Cerca de la pared lo recortamos para que la cámara
        // no cruce CAMERA_SAFE_RADIUS_XZ. La distancia mínima cae con
        // el max para evitar conflicto min > max.
        const baseMax = baseMaxRef.current ?? controls.maxDistance ?? 11
        const baseMin = baseMinRef.current ?? controls.minDistance ?? 4
        const avatarDistFromOrigin = Math.sqrt(
            pos[0] * pos[0] + pos[2] * pos[2]
        )
        const allowedMax = Math.max(
            CAMERA_MIN_FALLBACK_DISTANCE,
            CAMERA_SAFE_RADIUS_XZ -
                avatarDistFromOrigin -
                CAMERA_DYNAMIC_PADDING
        )
        const dynamicMax = Math.min(baseMax, allowedMax)
        const dynamicMin = Math.min(baseMin, dynamicMax * 0.7)
        if (Math.abs(controls.maxDistance - dynamicMax) > 0.05) {
            controls.maxDistance = dynamicMax
        }
        if (Math.abs(controls.minDistance - dynamicMin) > 0.05) {
            controls.minDistance = dynamicMin
        }

        controls.update()

        // Clamp de seguridad post-OrbitControls: si por alguna razón
        // la cámara terminó fuera del biopolímero (avatar al boundary
        // + orbit largo + ángulo extremo), la traemos de vuelta a
        // la esfera segura. El clamp se hace SOBRE la posición ya
        // settleada por OrbitControls, así no peleamos con su
        // damping. Si la cámara está bien adentro, este clamp es no-op.
        const camX = camera.position.x
        const camY = camera.position.y
        const camZ = camera.position.z
        const distXZ = Math.sqrt(camX * camX + camZ * camZ)
        let needClamp = false
        if (distXZ > CAMERA_SAFE_RADIUS_XZ) {
            const k = CAMERA_SAFE_RADIUS_XZ / Math.max(0.0001, distXZ)
            camera.position.x = camX * k
            camera.position.z = camZ * k
            needClamp = true
        }
        if (camY > CAMERA_SAFE_MAX_Y) {
            camera.position.y = CAMERA_SAFE_MAX_Y
            needClamp = true
        } else if (camY < CAMERA_SAFE_MIN_Y) {
            camera.position.y = CAMERA_SAFE_MIN_Y
            needClamp = true
        }
        if (needClamp) {
            controls.update()
        }
    })

    return null
}
